import { Close as CloseIcon } from "@mui/icons-material";
import {
  Stack,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DefaultLoader from "components/common/DefaultLoader";
import InlineSearch from "components/common/InlineSearch";
import Options from "components/common/Options";
import _ from "lodash";
import { useMemo, useState, useEffect } from "react";
import * as claimsService from "services/claimsService";
import * as staticDataService from "services/staticDataService";

const ExposureIndustryClassLinkerDialog = ({
  open,
  close,
  selectedExposure,
}) => {
  const theme = useTheme();
  const {
    data: defaultMeasures,
    isLoading: defaultMeasuresLoading,
    refetch: refetchDefaultMeasures,
  } = claimsService.useDefaultMeasuresByIndustryClassQuery();

  const {
    data: industryClasses = [],
    isLoading: industryClassesLoading,
  } = claimsService.useIndustryClassesQuery();

  const [
    triggerUpdateConfig,
  ] = staticDataService.useAdminUpdateConfigMutation();

  const [searchIndustryClass, setSearchIndustryClass] = useState("");
  const [selectedIndustryClasses, setSelectedIndustryClasses] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const isLoading =
    defaultMeasuresLoading || industryClassesLoading || !isInitialized;

  useEffect(() => {
    if (selectedExposure) {
      const industryClassesWithThisExposure = [];

      const { ALL, ...defaultMeasuresExcludingAll } = defaultMeasures || {};
      Object.entries(defaultMeasuresExcludingAll || {}).forEach(
        ([industryClassKey, exposureKeys]) => {
          if (exposureKeys.includes(selectedExposure.key)) {
            industryClassesWithThisExposure.push(industryClassKey);
          }
        }
      );

      setSelectedIndustryClasses(industryClassesWithThisExposure);
      setIsInitialized(true);
    }
  }, [selectedExposure, defaultMeasures]);

  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);

  const filteredIndustryClasses = useMemo(() => {
    if (!industryClasses) return [];
    return industryClasses.filter((industryClass) =>
      industryClass.name
        .toLowerCase()
        .includes(searchIndustryClass.toLowerCase())
    );
  }, [industryClasses, searchIndustryClass]);

  const industryClassItems = useMemo(() => {
    return filteredIndustryClasses.map((industryClass) => ({
      ...industryClass,
      id: industryClass.key,
      label: industryClass.name,
      selected: selectedIndustryClasses.some((ic) => ic === industryClass.key),
    }));
  }, [filteredIndustryClasses, selectedIndustryClasses]);

  const handleToggleIndustryClass = (industryClass) => {
    setSelectedIndustryClasses((prev) => {
      const exists = prev.some((ic) => ic === industryClass.key);
      if (exists) {
        return prev.filter((ic) => ic !== industryClass.key);
      } else {
        return [...prev, industryClass.key];
      }
    });
  };

  const hasChanges = useMemo(() => {
    if (!selectedExposure) return false;

    const currentIndustryClasses = [];
    Object.entries(defaultMeasures || {}).forEach(
      ([industryClassKey, exposureKeys]) => {
        if (exposureKeys.includes(selectedExposure.key)) {
          currentIndustryClasses.push(industryClassKey);
        }
      }
    );

    return !_.isEqual(
      [...currentIndustryClasses].sort(),
      [...selectedIndustryClasses].sort()
    );
  }, [selectedExposure, defaultMeasures, selectedIndustryClasses]);

  const handleSave = async () => {
    if (!selectedExposure || !hasChanges) return;

    setIsSaving(true);
    setError(null);

    try {
      const updatedDefaultMeasures = _.cloneDeep(defaultMeasures || {});

      Object.keys(updatedDefaultMeasures).forEach((industryClassKey) => {
        if (Array.isArray(updatedDefaultMeasures[industryClassKey])) {
          updatedDefaultMeasures[industryClassKey] = updatedDefaultMeasures[
            industryClassKey
          ].filter((exposureKey) => exposureKey !== selectedExposure.key);
        }
      });

      selectedIndustryClasses.forEach((industryClassKey) => {
        if (!updatedDefaultMeasures[industryClassKey]) {
          updatedDefaultMeasures[industryClassKey] = [];
        }
        if (
          !updatedDefaultMeasures[industryClassKey].includes(
            selectedExposure.key
          )
        ) {
          updatedDefaultMeasures[industryClassKey].push(selectedExposure.key);
        }
      });

      await triggerUpdateConfig({
        key: "exposures",
        value: {
          defaultMeasuresByIndustryClass: updatedDefaultMeasures,
        },
      });

      await refetchDefaultMeasures();

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(
        "An error has occurred when saving, please contact support at: support@cactus.bm"
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedExposure) {
    return null;
  }

  return (
    <Dialog open={open} onClose={close} maxWidth={"md"} fullWidth>
      <DialogTitle>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography variant={"h6"}>
            {`Industry Classes for ${selectedExposure.name}`}
          </Typography>
          <IconButton onClick={close} edge={"end"}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "white" }}>
        {isLoading ? (
          <Box display={"flex"} justifyContent={"center"} p={4}>
            <DefaultLoader color={theme.brand.white} />
          </Box>
        ) : error ? (
          <Alert severity={"error"} sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Stack spacing={2}>
            <InlineSearch
              placeholder={"Search industry classes..."}
              onChange={(text) => setSearchIndustryClass(text.toLowerCase())}
              fullWidth
            />

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 2,
                minHeight: 300,
                maxHeight: 400,
                overflow: "auto",
              }}
            >
              {industryClassItems.length > 0 ? (
                <>
                  <Typography
                    variant={"caption"}
                    display={"block"}
                    color={"textSecondary"}
                    gutterBottom={true}
                  >
                    {`${selectedIndustryClasses.length} of ${industryClasses.length} industry classes selected`}
                  </Typography>
                  <Options
                    items={industryClassItems}
                    onClick={handleToggleIndustryClass}
                  />
                </>
              ) : (
                <Typography
                  color={"textSecondary"}
                  textAlign={"center"}
                  sx={{ mt: 2 }}
                >
                  {"No industry classes found"}
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {saved && (
          <Alert severity={"success"} sx={{ my: 0, flex: 1 }}>
            {"Changes saved successfully!"}
          </Alert>
        )}
        <Box sx={{ display: "flex", flex: 1, justifyContent: "flex-end" }}>
          <Button
            onClick={handleSave}
            variant={"contained"}
            color={"primary"}
            disabled={isSaving || !hasChanges || !isInitialized}
          >
            {isSaving ? <DefaultLoader color={theme.brand.white} /> : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ExposureIndustryClassLinkerDialog;
